import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, Grid, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { API_URLS } from '../config/api';
import { useCart } from '../contexts/CartContext';

function Table() {
  const navigate = useNavigate();
  const { setSelectedTableNum, setOrderDetailsForOpenNum, updateTableNums } = useCart();
  const [tableNums, setTableNums] = useState([]);
  const [orders, setOrders] = useState([]); // 添加订单数据状态
  const [orderDetails, setOrderDetails] = useState({}); // 添加订单详情数据状态，key为openNum
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false); // 跟踪数据是否完全加载
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // 添加请求缓存状态，防止重复请求
  const [fetchingDetails, setFetchingDetails] = useState(new Set());
  // 添加订单列表缓存时间戳，避免频繁请求
  const [lastOrdersFetch, setLastOrdersFetch] = useState(0);
  const ORDER_CACHE_DURATION = 10000; // 10秒缓存时间
  
  // 添加背景图片状态
  const [backgroundImageLoaded, setBackgroundImageLoaded] = useState(false);
  
  // 检测背景图片是否能够加载
  useEffect(() => {
    const img = new Image();
    const imagePath = `${process.env.PUBLIC_URL}/images/background.jpg`;
    console.log('尝试加载背景图片:', imagePath);
    
    img.onload = () => {
      console.log('背景图片加载成功:', imagePath);
      setBackgroundImageLoaded(true);
    };
    img.onerror = () => {
      console.warn('背景图片加载失败，使用fallback背景. 图片路径:', imagePath);
      setBackgroundImageLoaded(false);
    };
    img.src = imagePath;
  }, []);
  
  // 确认对话框状态
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: '', // 'open' 或 'close'
    tableId: null,
    tableNum: ''
  });

  // 获取指定桌台的订单详情 - 使用桌台的openNum
  const fetchOrderDetailForTable = useCallback(async (openNum) => {
    // 如果已经有缓存数据，直接返回
    if (orderDetails[openNum]) {
      return orderDetails[openNum];
    }
    
    // 如果正在请求中，避免重复请求
    if (fetchingDetails.has(openNum)) {
      return [];
    }

    try {
      // 标记正在请求
      setFetchingDetails(prev => new Set([...prev, openNum]));
      
      const response = await fetch(`${API_URLS.SEARCH_ORDER_DETAIL}?openNum=${openNum}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.code === 200 && data.data) {
        // 缓存结果
        setOrderDetails(prev => ({
          ...prev,
          [openNum]: data.data
        }));
        // 同时存储到Context中
        setOrderDetailsForOpenNum(openNum, data.data);
        return data.data;
      }
      return [];
    } catch (error) {
      console.error('获取订单详情失败:', error);
      return [];
    } finally {
      // 移除请求标记
      setFetchingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(openNum);
        return newSet;
      });
    }
  }, [orderDetails, fetchingDetails, setOrderDetailsForOpenNum]);

  // 获取指定桌台的订单列表 - 使用openNum参数
  const fetchOrdersForTable = useCallback(async (openNum) => {
    try {
      const response = await fetch(API_URLS.SEARCH_ORDER_LIST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ openNum: openNum }),
      });
      const data = await response.json();
      if (data.code === 200 && data.data) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error('获取桌台订单失败:', error);
      return [];
    }
  }, []);

  // 批量获取所有已开台桌台的订单数据
  const fetchAllTableOrders = useCallback(async (tables) => {
    // 检查缓存，避免频繁请求
    const now = Date.now();
    if (now - lastOrdersFetch < ORDER_CACHE_DURATION) {
      return; // 在缓存期内，不重复请求
    }

    try {
      // 获取所有已开台的桌台 - 修复字段名：openNumber
      const openTables = tables.filter(table => table.isOpen && (table.openNumber || table.openNum));
      
      if (openTables.length === 0) {
        setOrders([]);
        setLastOrdersFetch(now);
        return;
      }

      // 并行获取所有已开台桌台的订单
      const orderPromises = openTables.map(async (table) => {
        const openNum = table.openNumber || table.openNum; // 兼容两种字段名
        const tableOrders = await fetchOrdersForTable(openNum);
        return tableOrders.map(order => ({
          ...order,
          tableNum: table.tableNum, // 添加桌号信息便于后续处理
          tableOpenNum: openNum
        }));
      });
      
      const allOrdersArrays = await Promise.all(orderPromises);
      const allOrders = allOrdersArrays.flat(); // 合并所有订单
      
      setOrders(allOrders);
      setLastOrdersFetch(now);
      
      // 获取订单详情
      const uniqueOpenNums = [...new Set(openTables.map(t => t.openNumber || t.openNum))];
      const uncachedOpenNums = uniqueOpenNums.filter(openNum => 
        !orderDetails[openNum] && !fetchingDetails.has(openNum)
      );
      
      if (uncachedOpenNums.length > 0) {
        // 并行获取未缓存的订单详情
        const detailsPromises = uncachedOpenNums.map(async (openNum) => {
          const details = await fetchOrderDetailForTable(openNum);
          return { openNum, details };
        });
        
        const allDetails = await Promise.all(detailsPromises);
        const detailsMap = {};
        allDetails.forEach(({ openNum, details }) => {
          detailsMap[openNum] = details;
        });
        
        // 合并到现有的订单详情中
        setOrderDetails(prev => ({
          ...prev,
          ...detailsMap
        }));
      }
      
      // 标记数据已完全加载
      setDataLoaded(true);
      
    } catch (error) {
      console.error('获取订单数据失败:', error);
      setOrders([]);
      setDataLoaded(true); // 即使失败也标记为已加载
    }
  }, [fetchOrdersForTable, fetchOrderDetailForTable, orderDetails, fetchingDetails, lastOrdersFetch, ORDER_CACHE_DURATION]);

  // 重新获取桌台数据的函数（简化版，避免依赖循环）
  const fetchTableNums = useCallback(async () => {
    try {
      const res = await fetch(API_URLS.TABLE_NUMBER_LIST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.code === 200 && data.data && data.data.length > 0) {
        setTableNums(data.data);
        // 同步更新CartContext中的桌台列表，避免重复请求
        updateTableNums(data.data);
      }
      setLoading(false);
    } catch (e) {
      setTableNums([]);
      setLoading(false);
    }
  }, [updateTableNums]);

  // 修复useEffect依赖，避免频繁重新调用
  useEffect(() => {
    let isMounted = true;
    
    const initData = async () => {
      if (isMounted) {
        // 先获取桌台数据
        try {
          const res = await fetch(API_URLS.TABLE_NUMBER_LIST, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
          });
          const data = await res.json();
          if (data.code === 200 && data.data && data.data.length > 0) {
            setTableNums(data.data);
            // 同步更新CartContext中的桌台列表，避免重复请求
            updateTableNums(data.data);
            
            // 直接在这里获取订单数据，避免函数依赖
            if (isMounted) {
              // 获取所有已开台的桌台
              const openTables = data.data.filter(table => table.isOpen && (table.openNumber || table.openNum));
              
              if (openTables.length > 0) {
                try {
                  // 并行获取所有已开台桌台的订单
                  const orderPromises = openTables.map(async (table) => {
                    const openNum = table.openNumber || table.openNum;
                    const response = await fetch(API_URLS.SEARCH_ORDER_LIST, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ openNum: openNum }),
                    });
                    const orderData = await response.json();
                    if (orderData.code === 200 && orderData.data) {
                      return orderData.data.map(order => ({
                        ...order,
                        tableNum: table.tableNum,
                        tableOpenNum: openNum
                      }));
                    }
                    return [];
                  });
                  
                  const allOrdersArrays = await Promise.all(orderPromises);
                  const allOrders = allOrdersArrays.flat();
                  setOrders(allOrders);
                  setLastOrdersFetch(Date.now());
                }catch (error) {
                  console.error('获取订单数据失败:', error);
                  setOrders([]);
                }
              } else {
                setOrders([]);
              }
              setDataLoaded(true);
            }
          }
        } catch (e) {
          setTableNums([]);
          setOrders([]);
          setDataLoaded(true);
        }
        
        setLoading(false);
      }
    };
    
    initData();
    
    return () => {
      isMounted = false;
    };
  }, []); // 移除所有依赖，只在组件挂载时执行一次

  // 获取指定桌台的订单
  const getTableOrders = (tableNum) => {
    if (!orders || orders.length === 0) return [];
    
    // 新的数据结构中已经添加了tableNum字段，优先使用
    return orders.filter(order => {
      return order.tableNum === tableNum ||
             order.tableNumber === tableNum || 
             order.table === tableNum ||
             String(order.tableNum) === String(tableNum) ||
             String(order.tableNumber) === String(tableNum);
    });
  };

  // 获取指定桌台的订单商品总数
  const getTableOrderItemCount = (tableNum) => {
    const tableOrders = getTableOrders(tableNum);
    if (tableOrders.length === 0) return 0;
    
    let totalCount = 0;
    tableOrders.forEach(order => {
      // 使用tableOpenNum或openNum字段查找订单详情
      const openNum = order.tableOpenNum || order.openNum;
      const details = orderDetails[openNum];
      if (details && Array.isArray(details) && details.length > 0) {
        details.forEach(item => {
          // 兼容不同的数量字段名，优先使用常见字段
          const itemCount = item.quantity || item.number || item.num || item.count || 1;
          totalCount += Number(itemCount);
        });
      } else {
        // 如果没有详细信息，但有订单，至少计为1个商品
        totalCount += 1;
      }
    });
    return totalCount;
  };

  // 获取桌台订单摘要信息（用于已点订单显示）
  const getTableOrderSummary = (tableNum) => {
    const tableOrders = getTableOrders(tableNum);
    if (tableOrders.length === 0) return null;
    
    let totalCount = 0;
    let totalAmount = 0;
    const itemNames = [];
    
    tableOrders.forEach(order => {
      // 使用tableOpenNum或openNum字段查找订单详情
      const openNum = order.tableOpenNum || order.openNum;
      const details = orderDetails[openNum];
      if (details && Array.isArray(details) && details.length > 0) {
        details.forEach(item => {
          // 使用与getTableOrderItemCount相同的逻辑
          const itemCount = item.quantity || item.number || item.num || item.count || 1;
          const itemPrice = item.price || item.unitPrice || item.amount || 0;
          totalCount += Number(itemCount);
          totalAmount += Number(itemCount) * Number(itemPrice);
          // 兼容不同的商品名字段
          const itemName = item.title || item.name || item.productName || item.itemName;
          if (itemName && itemNames.length < 3) { // 最多显示3个商品名
            itemNames.push(itemName);
          }
        });
      } else {
        // 如果没有详细信息，但有订单，至少计为1个商品
        totalCount += 1;
        totalAmount += 0; // 没有详细信息时无法计算金额
      }
    });
    
    return {
      totalCount,
      totalAmount: totalAmount || 0,
      itemNames,
      orderCount: tableOrders.length
    };
  };





  const handleOrderClick = (table, event) => {
    event.stopPropagation();
    
    if (!table.isOpen) {
      setSnackbarMessage('请先开台后再点单');
      setSnackbarOpen(true);
      return;
    }
    
    setSelectedTableNum(table.tableNum);
    navigate(`/products?table=${table.tableNum}`);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // 显示开台确认对话框
  const handleOpenTableConfirm = (tableId, tableNum, event) => {
    event.stopPropagation();
    setConfirmDialog({
      open: true,
      type: 'open',
      tableId: tableId,
      tableNum: tableNum
    });
  };

  // 显示关台确认对话框
  const handleCloseTableConfirm = (tableId, tableNum, event) => {
    event.stopPropagation();
    setConfirmDialog({
      open: true,
      type: 'close',
      tableId: tableId,
      tableNum: tableNum
    });
  };

  // 确认对话框关闭
  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      open: false,
      type: '',
      tableId: null,
      tableNum: ''
    });
  };

  // 执行开台操作
  const executeOpenTable = async () => {
    try {
      const response = await fetch(`${API_URLS.TABLE_NUMBER_LIST.replace('/searchTableNumList', '/openTable')}?id=${confirmDialog.tableId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.code === 200) {
        // 重新获取桌台数据和订单数据
        const res = await fetch(API_URLS.TABLE_NUMBER_LIST, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (data.code === 200 && data.data && data.data.length > 0) {
          setTableNums(data.data);
          updateTableNums(data.data);
          
          // 刷新订单数据
          const openTables = data.data.filter(table => table.isOpen && (table.openNumber || table.openNum));
          if (openTables.length > 0) {
            try {
              const orderPromises = openTables.map(async (table) => {
                const openNum = table.openNumber || table.openNum;
                const response = await fetch(API_URLS.SEARCH_ORDER_LIST, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ openNum: openNum }),
                });
                const orderData = await response.json();
                if (orderData.code === 200 && orderData.data) {
                  return orderData.data.map(order => ({
                    ...order,
                    tableNum: table.tableNum,
                    tableOpenNum: openNum
                  }));
                }
                return [];
              });
              
              const allOrdersArrays = await Promise.all(orderPromises);
              const allOrders = allOrdersArrays.flat();
              setOrders(allOrders);
              setLastOrdersFetch(Date.now());
            } catch (error) {
              console.error('刷新订单数据失败:', error);
            }
          } else {
            setOrders([]);
          }
        }
        setSnackbarMessage(`${confirmDialog.tableNum} 开台成功！`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('开台失败:', error);
      setSnackbarMessage('开台失败，请重试');
      setSnackbarOpen(true);
    }
    handleConfirmDialogClose();
  };

  // 执行关台操作
  const executeCloseTable = async () => {
    try {
      const response = await fetch(`${API_URLS.TABLE_NUMBER_LIST.replace('/searchTableNumList', '/closeTable')}?id=${confirmDialog.tableId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.code === 200) {
        // 重新获取桌台数据和订单数据
        const res = await fetch(API_URLS.TABLE_NUMBER_LIST, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });
        const tableData = await res.json();
        if (tableData.code === 200 && tableData.data && tableData.data.length > 0) {
          setTableNums(tableData.data);
          updateTableNums(tableData.data);
          
          // 刷新订单数据
          const openTables = tableData.data.filter(table => table.isOpen && (table.openNumber || table.openNum));
          if (openTables.length > 0) {
            try {
              const orderPromises = openTables.map(async (table) => {
                const openNum = table.openNumber || table.openNum;
                const response = await fetch(API_URLS.SEARCH_ORDER_LIST, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ openNum: openNum }),
                });
                const orderData = await response.json();
                if (orderData.code === 200 && orderData.data) {
                  return orderData.data.map(order => ({
                    ...order,
                    tableNum: table.tableNum,
                    tableOpenNum: openNum
                  }));
                }
                return [];
              });
              
              const allOrdersArrays = await Promise.all(orderPromises);
              const allOrders = allOrdersArrays.flat();
              setOrders(allOrders);
              setLastOrdersFetch(Date.now());
            } catch (error) {
              console.error('刷新订单数据失败:', error);
            }
          } else {
            setOrders([]);
          }
        }
        setSnackbarMessage(`${confirmDialog.tableNum} 关台成功！`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('关台失败:', error);
      setSnackbarMessage('关台失败，请重试');
      setSnackbarOpen(true);
    }
    handleConfirmDialogClose();
  };

  // 确认操作
  const handleConfirmAction = () => {
    if (confirmDialog.type === 'open') {
      executeOpenTable();
    } else if (confirmDialog.type === 'close') {
      executeCloseTable();
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#0a0f1c',
        flexDirection: 'column'
      }}>
        <Box sx={{
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: '4px solid #ffc107',
          borderTop: '4px solid transparent',
          animation: 'spin 1s linear infinite',
          marginBottom: '24px'
        }} />
        <Typography sx={{ 
          color: '#e2e8f0',
          fontSize: '18px', 
          fontWeight: 500,
          letterSpacing: '0.5px'
        }}>
          正在加载...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#0a0f1c',
      backgroundImage: backgroundImageLoaded ? [
        `linear-gradient(rgba(5, 10, 20, 0.45), rgba(10, 15, 28, 0.5))`,
        `url('${process.env.PUBLIC_URL || ''}/images/background.jpg')`
      ].join(', ') : `linear-gradient(135deg, 
        rgba(5, 10, 20, 0.95) 0%, 
        rgba(10, 15, 28, 0.9) 25%,
        rgba(15, 25, 45, 0.85) 50%,
        rgba(20, 30, 50, 0.9) 75%,
        rgba(10, 15, 28, 0.95) 100%
      )`,
      backgroundSize: { xs: 'cover', sm: 'cover', md: 'cover' },
      backgroundPosition: { xs: 'center 20%', sm: 'center center', md: 'center center' },
      backgroundRepeat: 'no-repeat',
      // 如果图片加载失败，backgroundColor会显示为fallback
      backgroundBlendMode: 'normal',
      padding: '24px 0',
      position: 'relative',
      width: '100%',
      overflow: 'hidden'
    }}>
      {/* 顶部标题区 */}
      <Box sx={{
        textAlign: 'center',
        marginBottom: '36px',
        padding: '0 20px'
      }}>
        <Typography variant="h2" sx={{ 
          color: '#ffc107',
          fontWeight: 700,
          marginBottom: '12px',
          fontSize: { xs: '32px', sm: '38px', md: '42px' },
          letterSpacing: '1px',
          textShadow: '0 2px 4px rgba(255, 193, 7, 0.3)'
        }}>
          🍸 麓Bar
        </Typography>
        <Typography variant="h5" sx={{ 
          color: '#e2e8f0',
          fontSize: { xs: '18px', sm: '20px', md: '22px' },
          fontWeight: 400,
          letterSpacing: '0.5px'
        }}>
          智能点单系统
        </Typography>
      </Box>
      
      {/* 主要内容区域 */}
      <Box sx={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* 针对iPad Pro优化的Grid布局 */}
        <Grid container spacing={3} justifyContent="center" sx={{
          // 确保iPad Pro上显示3个卡片
          '@media (min-width: 768px) and (max-width: 1024px)': {
            '& .MuiGrid-item': {
              maxWidth: 'calc(33.333% - 16px)',
              flexBasis: 'calc(33.333% - 16px)'
            }
          }
        }}>
          {tableNums.map((table) => {
            const tableOrders = getTableOrders(table.tableNum);
            const orderItemCount = getTableOrderItemCount(table.tableNum);
            const orderSummary = getTableOrderSummary(table.tableNum);
            

            
            return (
              <Grid item xs={12} sm={6} md={4} key={table.tableNum}>
                <Card 
                  sx={{
                    backgroundColor: 'rgba(26, 35, 50, 0.5)',
                    backdropFilter: 'blur(15px)',
                    border: `2px solid ${table.isOpen ? '#ffc107' : '#4a5568'}`,
                    borderRadius: '16px',
                    minHeight: '300px',
                    transition: 'all 0.2s ease',
                    cursor: 'default',
                    boxShadow: table.isOpen 
                      ? '0 6px 20px rgba(255, 193, 7, 0.4), 0 0 0 1px rgba(255, 193, 7, 0.2), 0 8px 32px rgba(0, 0, 0, 0.6)'
                      : '0 3px 12px rgba(0, 0, 0, 0.5), 0 8px 32px rgba(0, 0, 0, 0.3)',
                    // 移除悬停效果，添加触摸激活效果
                    '&:active': {
                      transform: 'scale(0.98)',
                      boxShadow: table.isOpen
                        ? '0 3px 12px rgba(255, 193, 7, 0.3), 0 4px 16px rgba(0, 0, 0, 0.7)'
                        : '0 2px 8px rgba(0, 0, 0, 0.6)'
                    }
                  }}
                >
                  <CardContent sx={{ 
                    padding: '20px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                  }}>
                    {/* 顶部状态栏 */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px',
                      gap: '16px' // 增加桌号和按钮之间的间隔
                    }}>
                      {/* 桌号标识 */}
                      <Box sx={{
                        backgroundColor: table.isOpen ? '#ffc107' : '#4a5568',
                        color: table.isOpen ? '#0a0f1c' : '#e2e8f0',
                        padding: '10px 16px',
                        borderRadius: '10px',
                        fontSize: '18px',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        minWidth: '60px',
                        textAlign: 'center',
                        boxShadow: table.isOpen 
                          ? '0 3px 8px rgba(255, 193, 7, 0.3)'
                          : '0 2px 6px rgba(0, 0, 0, 0.2)'
                      }}>
                        {table.tableNum}
                      </Box>
                      
                      {/* 开台/关台按钮 */}
                      {table.isOpen ? (
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={(e) => handleCloseTableConfirm(table.id, table.tableNum, e)}
                          sx={{
                            backgroundColor: '#dc2626',
                            color: '#fff',
                            borderRadius: '8px',
                            padding: '12px 20px',
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            minHeight: '44px', // 适合触摸的最小高度
                            boxShadow: '0 3px 8px rgba(220, 38, 38, 0.3)',
                            '&:active': {
                              backgroundColor: '#b91c1c',
                              transform: 'scale(0.95)',
                              boxShadow: '0 2px 4px rgba(220, 38, 38, 0.4)'
                            }
                          }}
                        >
                          关台
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={(e) => handleOpenTableConfirm(table.id, table.tableNum, e)}
                          sx={{
                            backgroundColor: '#ffc107',
                            color: '#0a0f1c',
                            borderRadius: '8px',
                            padding: '12px 20px',
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            minHeight: '44px', // 适合触摸的最小高度
                            boxShadow: '0 3px 8px rgba(255, 193, 7, 0.3)',
                            '&:active': {
                              backgroundColor: '#f59e0b',
                              transform: 'scale(0.95)',
                              boxShadow: '0 2px 4px rgba(255, 193, 7, 0.4)'
                            }
                          }}
                        >
                          开台
                        </Button>
                      )}
                    </Box>

                    {/* 点单区域 */}
                    <Box 
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minHeight: table.isOpen && orderItemCount > 0 ? '136px' : '120px', // 调整为平等高度布局
                      }}
                    >
                      {/* 点单按钮 */}
                      <Box
                        sx={{
                          backgroundColor: table.isOpen ? '#ffc107' : '#374151',
                          borderRadius: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: table.isOpen ? 'pointer' : 'not-allowed',
                          opacity: table.isOpen ? 1 : 0.7,
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          height: table.isOpen && orderItemCount > 0 ? '60px' : '120px', // 调整为与已点订单按钮平等高度
                          // 移除悬停效果，添加触摸激活效果
                          '&:active': table.isOpen ? {
                            backgroundColor: '#f59e0b',
                            transform: 'scale(0.98)'
                          } : {},
                          // 保留开台状态的闪烁效果
                          '&::before': table.isOpen ? {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                            animation: 'shimmer 3s infinite'
                          } : {}
                        }}
                        onClick={(e) => handleOrderClick(table, e)}
                      >
                        <Typography sx={{ 
                          color: table.isOpen ? '#0a0f1c' : '#e2e8f0',
                          fontSize: table.isOpen && orderItemCount > 0 ? '16px' : '20px', // 调整字体大小适应新的按钮高度
                          fontWeight: 700,
                          marginBottom: '4px',
                          letterSpacing: '0.5px',
                          textAlign: 'center'
                        }}>
                          {table.isOpen ? '🍻 点单' : '请先开台'}
                        </Typography>
                        {table.isOpen && (
                          <Typography sx={{ 
                            color: 'rgba(10, 15, 28, 0.8)',
                            fontSize: table.isOpen && orderItemCount > 0 ? '10px' : '12px', // 调整字体大小适应新的按钮高度
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            textAlign: 'center'
                          }}>
                            畅享美酒时光
                          </Typography>
                        )}
                      </Box>

                      {/* 已点订单区域 */}
                      {table.isOpen && orderItemCount > 0 && orderSummary && dataLoaded && (
                        <Box 
                          onClick={(e) => {
                            e.stopPropagation();
                            // 使用桌台数据中的openNumber或openNum字段（来自/tableNumberManage/searchTableNumberList接口）
                            const openNum = table.openNumber || table.openNum;
                            if (openNum) {
                              navigate(`/order-detail?openNum=${openNum}&table=${table.tableNum}`);
                            } else {
                              // 如果桌台数据中没有openNumber/openNum，显示提示
                              console.error('桌台数据中未找到openNumber/openNum字段', {
                                tableNum: table.tableNum,
                                tableData: table,
                                orderItemCount: orderItemCount
                              });
                              alert('开台信息不完整，请重新开台后再试');
                            }
                          }}
                          sx={{ 
                            backgroundColor: 'rgba(255, 193, 7, 0.1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            height: '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0 12px',
                            '&:active': {
                              backgroundColor: 'rgba(255, 193, 7, 0.2)',
                              transform: 'scale(0.98)'
                            }
                          }}
                        >
                          <Typography sx={{ 
                            color: '#ffc107',
                            fontSize: '14px',
                            fontWeight: 600,
                            letterSpacing: '0.3px',
                            textAlign: 'center',
                            width: '100%'
                          }}>
                            📋 已点订单
                          </Typography>
                        </Box>
                      )}
                      

                    </Box>

                    {/* 底部状态信息 */}
                    <Box sx={{ 
                      marginTop: '16px',
                      padding: '12px 16px',
                      backgroundColor: 'rgba(255, 193, 7, 0.1)',
                      borderRadius: '8px',
                      textAlign: 'center',
                      border: `1px solid ${table.isOpen ? 'rgba(255, 193, 7, 0.3)' : 'rgba(74, 85, 104, 0.3)'}`
                    }}>
                      <Typography sx={{ 
                        color: table.isOpen ? '#ffc107' : '#94a3b8',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: '0.5px',
                        lineHeight: 1.4
                      }}>
                        {table.isOpen ? (
                          <>
                            <span style={{ marginRight: '6px' }}>🕐</span>
                            {table.openTime ? `开台时间: ${table.openTime}` : '营业中'}
                          </>
                        ) : (
                          <>
                            <span style={{ marginRight: '6px' }}>💤</span>
                            空闲中
                          </>
                        )}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      
      {/* 确认对话框 */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmDialogClose}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(26, 35, 50, 0.75)',
            backdropFilter: 'blur(20px)',
            color: '#e2e8f0',
            borderRadius: '16px',
            border: '2px solid #ffc107',
            minWidth: '320px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 193, 7, 0.3)'
          }
        }}
      >
        <DialogTitle sx={{
          color: '#ffc107',
          fontSize: '18px',
          fontWeight: 600,
          textAlign: 'center',
          borderBottom: '1px solid rgba(255, 193, 7, 0.2)',
          paddingBottom: '16px'
        }}>
          {confirmDialog.type === 'open' ? '🍸 确认开台' : '💤 确认关台'}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', textAlign: 'center' }}>
          <Typography sx={{
            color: '#e2e8f0',
            fontSize: '16px',
            lineHeight: 1.6
          }}>
            {confirmDialog.type === 'open' 
              ? `确认要为 ${confirmDialog.tableNum} 开台吗？`
              : `确认要为 ${confirmDialog.tableNum} 关台吗？`
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          paddingBottom: '24px',
          gap: '12px'
        }}>
          <Button
            onClick={handleConfirmDialogClose}
            sx={{
              backgroundColor: 'transparent',
              color: '#94a3b8',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              border: '1px solid rgba(148, 163, 184, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(148, 163, 184, 0.1)',
                border: '1px solid rgba(148, 163, 184, 0.5)'
              }
            }}
          >
            取消
          </Button>
          <Button
            onClick={handleConfirmAction}
            sx={{
              backgroundColor: '#ffc107',
              color: '#0a0f1c',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              border: 'none',
              '&:hover': {
                backgroundColor: '#f59e0b'
              }
            }}
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarMessage.includes('成功') ? 'success' : 'warning'}
          sx={{ 
            width: '100%',
            backgroundColor: 'rgba(26, 35, 50, 0.8)',
            backdropFilter: 'blur(12px)',
            color: snackbarMessage.includes('成功') ? '#10b981' : '#ffc107',
            border: `2px solid ${snackbarMessage.includes('成功') ? '#10b981' : '#ffc107'}`,
            borderRadius: '12px',
            fontSize: '16px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
            '& .MuiAlert-icon': {
              color: snackbarMessage.includes('成功') ? '#10b981' : '#ffc107'
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* 动画样式 */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
          }
        `}
      </style>
    </Box>
  );
}

export default Table; 